import axios, { AxiosResponse } from 'axios'
// import { MessageBox, Message } from 'element-ui'
import { RefreshToken } from '@/api/entrance'
import { ping } from '@/api/ping'
import store from '@/store'
import {
  getAccessToken,
  getCSRF,
  getRefreshToken,
  setRefreshToken,
} from '@/utils/auth'
import Vue from 'vue'

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
})

function blobToJSON(blob: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(JSON.parse(reader.result as string))
    }

    reader.onerror = () => {
      reject(reader.error)
    }

    reader.readAsText(blob)
  })
}

function handleAttachmentResponse(res: AxiosResponse): boolean {
  if (!res.headers) {
    return false
  }

  if (!res.headers['content-disposition']) {
    return false
  }

  if (
    res.headers['content-disposition'].split(';').indexOf('attachment') == -1
  ) {
    return false
  }

  let filename: string = res.headers['content-disposition'].split(
    'filename='
  )[1]
  filename = filename.substr(1, filename.length - 2)

  const url = window.URL.createObjectURL(
    new Blob([res.data], {
      type: res.headers['content-type'],
    })
  )
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()

  return true
}

function handleDownload(url: string) {
  window.open(`/api/v1/download/${url}`, '_blank')
}

//
// request interceptor
// lakukan sesuatu sebelum proses request
service.interceptors.request.use(
  async (config) => {
    config.withCredentials = true
    // Jika request dengan method POST, PUT, PATCH, DELETE
    // Tambahkan CSRF token.
    // Jika belum punya CSRF token atau CSRF token sudah expired, ambil CSRF token dari server
    if (
      typeof config.method == 'string' &&
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())
    ) {
      if (typeof getCSRF() !== 'string') {
        await ping()
      }
      config.headers['X-XSRF-Token'] = getCSRF()
    }

    // Jika sudah memiliki token, tambahkan token pada request.
    const accessToken = getAccessToken()

    if (typeof accessToken == 'string' && accessToken != '') {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    } else if (config.url != '/ping') {
      const refreshToken = getRefreshToken()
      if (typeof refreshToken == 'string' && refreshToken != '') {
        const { data, code } = await RefreshToken(refreshToken)
        if (typeof data == 'object' && code === 20201) {
          setRefreshToken(data.refresh_token)
        }
      }
    }

    return config
  },
  (error) => {
    // Jika terjadi error dengan request,
    return Promise.reject(error)
  }
)

// response interceptor
service.interceptors.response.use(
  async (response) => {
    if (handleAttachmentResponse(response)) {
      return response.data
    }

    let res = response.data

    if (
      res instanceof Blob &&
      res.type &&
      res.type.toLowerCase().indexOf('json') != -1
    ) {
      res = await blobToJSON(res)
    }

    if (!(typeof res == 'object' && res.code)) {
      return res
    }

    // if the custom code is not 20000, it is judged as an error.
    if (res.code === 20000) {
      return res.data
    } else {
      // 50008: Illegal token; 50012: Other clients logged in; 50014: Token expired;
      switch (res.code) {
        case 20201:
          // eslint-disable-next-line
          ;(Vue as any).$toast.info('Created')
          return res.data || res.status
        case 20202:
          // eslint-disable-next-line
          ;(Vue as any).$toast.info('Updated')
          return res.data || res.status
        case 20203:
          // eslint-disable-next-line
          ;(Vue as any).$toast.info('Deleted')
          return res.data || res.status

        case 20210:
          handleDownload(res.data)
          return Promise.resolve()
        case 50008:
        case 50012:
        case 50014:
          // to re-login
          // eslint-disable-next-line
          ;(Vue as any).$bvModal
            .msgBoxConfirm(
              'You have been logged out, you can cancel to stay on this page, or log in again',
              { title: 'Confirm logout' }
            )
            .then(() => {
              store.dispatch('user/resetToken').then(() => {
                location.reload()
              })
            })
          break
        case 20301:
          location.href = process.env.BASE_URL + res.data
          return
        case 20400:
        case 20404:
        case 20500:
        case 20503:
          // eslint-disable-next-line
          ;(Vue as any).$toast.error(
            res.data ? res.data.message : res.status || 'Something wrong'
          )
          break
        case 20401:
          // eslint-disable-next-line
          ;(Vue as any).$toast.error(
            res.data ? res.data.message : res.status || 'Unauthorized'
          )
          store.dispatch('user/resetToken').then(() => {
            location.reload()
          })
          break
      }
      return Promise.reject(res)
    }
  },
  async (error) => {
    if (error.response && error.response) {
      switch (error.response.status) {
        case 401:
          await store.dispatch('user/resetToken')
          return Promise.reject(error)
      }
    }

    console.log('err' + error) // for debug
    // eslint-disable-next-line
    ;(Vue as any).$toast.error(error.message || 'Something wrong :-(')
  }
)

export default service

const basicRequest = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 60000,
})

basicRequest.interceptors.request.use(async (config) => {
  if (
    typeof config.method == 'string' &&
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())
  ) {
    if (typeof getCSRF() !== 'string') {
      await ping()
    }
    config.headers['X-XSRF-Token'] = getCSRF()
  }

  return config
})

basicRequest.interceptors.response.use((response) => response.data)

export { basicRequest }
